import { isRedoEmailAddress } from "@redotech/account-common/redo-domains";
import { RedoFeatureNotificationCard } from "@redotech/arbiter/feature-notification/redo-feature-notification-card";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { memo, useContext, useState } from "react";
import { useLocation } from "react-router-dom";
import * as styles from "./returns-nav-rearrangement-popup.module.css";

const LOCAL_STORAGE_KEY = "redo.returns-nav-rearrangement-dismissed";

function readDismissed(): boolean {
  try {
    return localStorage.getItem(LOCAL_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function writeDismissed() {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, "true");
  } catch {
    // localStorage unavailable; fall back to in-memory dismissal.
  }
}

export const ReturnsNavRearrangementPopup = memo(
  function ReturnsNavRearrangementPopup() {
    const { t } = useTranslation();
    const team = useContext(TeamContext);
    const user = useContext(UserContext);
    const { pathname } = useLocation();

    const [dismissed, setDismissed] = useState(() => readDismissed());

    const handleDismiss = useHandler(() => {
      setDismissed(true);
      writeDismissed();
    });

    if (dismissed) {
      return null;
    }
    if (isRedoEmailAddress(user?.email)) {
      return null;
    }
    if (!pathname.includes("/returns")) {
      return null;
    }
    if (!team) {
      return null;
    }

    const giftReceiptsEnabled = !!team.settings.returns?.enableGiftReceipts;
    const instantExchangeEnabled = !!team.settings.exchanges?.allowInstant;
    if (!giftReceiptsEnabled && !instantExchangeEnabled) {
      return null;
    }

    const description =
      giftReceiptsEnabled && instantExchangeEnabled
        ? t(
            "We've moved Gift receipt requests and Instant exchange recovery into the Needs attention folder under Returns.",
          )
        : giftReceiptsEnabled
          ? t(
              "We've moved Gift receipt requests into the Needs attention folder under Returns.",
            )
          : t(
              "We've moved Instant exchange recovery into the Needs attention folder under Returns.",
            );

    return (
      <div className={styles.container}>
        <RedoFeatureNotificationCard
          actionText={t("Got it")}
          description={description}
          onAction={handleDismiss}
          onDismiss={handleDismiss}
          title={t("We've tidied up the Returns nav")}
        />
      </div>
    );
  },
);
